import { Component, computed, effect, inject, Injector, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  CiscoPbxExport,
  CucAutoAttendantsReport,
  CucmDirectoryNumber,
  CucmDirectoryNumbersReport,
  CucmHuntList,
  DeviceUsageType,
  JobState,
  JobStatus,
  LineUsageType,
  MigrationComplexityScore,
  MigrationStatus,
  SuggestedProduct,
} from '../../../shared/models/generated/ziroModels';
import { BottomNavService, BottomNavUpdateButtonsList } from '../../../shared/bottom-nav/bottom-nav.service';
import { ButtonStyles } from '../../../button/button.component';
import { combineLatest, forkJoin, Subscription, switchMap } from 'rxjs';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { ChartData, DisabledChartText } from '../../migrations-cards/migrations-card-abstract.directive';
import { MigrationsCucmHuntListsContext } from '../../services/cisco/migrations-cucm-hunt-lists.context';
import { MigrationsCucmDirectoryNumberContext } from '../../services/cisco/migrations-cucm-directory-number.context';
import moment from 'moment';
import {
  COLOR_PALETTE,
  MigrationsService,
  UiDeviceType,
  UiLineType,
  UiMigrationStatus,
} from '../../services/migrations.service';
import { MigrationsCucAutoAttendantsContext } from '../../services/cisco/migrations-cuc-auto-attendants.context';
import { ToastService } from '../../../shared/services/toast.service';
import { ToastTypes } from '../../../shared/services/abstract/toast.service.abstract';
import { MigrationsAssessmentsContext } from '../../services/migrations-assessments.context';
import { CustomColor, MigrationsDashboardService } from '../../services/migrations-dashboard.service';
import { TranslateService } from '@ngx-translate/core';

export type WidgetFilter = 'lineType' | 'deviceType' | 'callingProduct' | 'migrationStatus';

@Component({
  selector: 'ziro-migrations-cisco-dashboard',
  templateUrl: 'migrations-cisco-dashboard.component.html',
  styleUrls: ['../../migrations.component.scss'],
  providers: [MigrationsService, MigrationsDashboardService],
  standalone: false,
})
export class MigrationsCiscoDashboardComponent implements OnInit, OnDestroy {
  private readonly _refreshButtonId = 'migrations-cisco-reports-list-refresh';

  private _route = inject(ActivatedRoute);
  private _router = inject(Router);
  private _translateService = inject(TranslateService);
  private _dashboardService = inject(MigrationsDashboardService);
  private _bottomNavService = inject(BottomNavService);
  private _toastService = inject(ToastService);
  private _migrationsAssessmentsContext = inject(MigrationsAssessmentsContext);
  private _migrationsCucmHuntListsContext = inject(MigrationsCucmHuntListsContext);
  private _migrationsCucmDirectoryNumberContext = inject(MigrationsCucmDirectoryNumberContext);
  private _migrationsCucAutoAttendantsContext: MigrationsCucAutoAttendantsContext | null = null;
  private _injector = inject(Injector);

  readonly MIN_CDR_DAYS = 30;
  private _assessmentUuid: string;
  private _assessment: CiscoPbxExport;
  private _subscription = new Subscription();
  private _dnReport: CucmDirectoryNumbersReport;
  private _huntLists: CucmHuntList[];
  private _dnJobStatus: JobState;
  private _huntGroupJobStatus: JobState;
  private _autoAttendantJobStatus: JobState;
  private _tenantId: string;

  colorScheme = this._dashboardService.createDefaultColorScheme();
  autoAttendantCustomColors: CustomColor[] = [{ name: 'require investigation', value: COLOR_PALETTE.danger }];
  usageTypeCustomColors = this._dashboardService.usageTypeCustomColors;
  deviceTypeCustomColors = this._dashboardService.deviceTypeCustomColors;
  migrationStatusCustomColors = this._dashboardService.migrationStatusCustomColors;

  migrationStatusData: ChartData[] = [];
  usageTypeData: ChartData[] = [];
  deviceTypeData: ChartData[] = [];
  huntListData: ChartData[] = [];
  suggestedProductData: ChartData[] = [];
  autoAttendantsData: ChartData[] = [];
  suggestedProductsDisabledText: DisabledChartText;
  autoAttendantsDisabledText: DisabledChartText;
  isLoading = true;
  isDnReportWidgetError = false;
  isHuntListError = false;
  cdrDiffDays: number = this.MIN_CDR_DAYS + 1;
  isCdrFileUploaded: boolean;
  isUnityFileUploaded: boolean;
  isAutoAttendantReportWidgetError = false;

  isDnReportDataRefreshing = signal<boolean>(true);
  isHuntListRefreshing = signal<boolean>(true);
  isAutoAttendantRefreshing = signal<boolean>(true);

  private _isRefreshing = computed(
    () => this.isDnReportDataRefreshing() || this.isHuntListRefreshing() || this.isAutoAttendantRefreshing(),
  );

  constructor() {
    effect(() => {
      this._dashboardService.setRefreshButtonPendingState(this._refreshButtonId, this._isRefreshing());
    });
  }

  ngOnInit(): void {
    this._assessmentUuid = this._route.snapshot.paramMap.get('id');
    this._tenantId = this._route.snapshot.paramMap.get('tenantId');
    this._initBottomNav();
    this._initContexts();
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

  onDnReportFilter(filter: string, filterType: WidgetFilter): void {
    // This navigation link for legend items is defined on the data passed into the child
    if (filter) {
      this._router.navigate(['cucm-directory-numbers-report', `${filterType}=${encodeURIComponent(filter)}`], {
        relativeTo: this._route,
      });
    } else {
      this._router.navigate(['cucm-directory-numbers-report'], { relativeTo: this._route });
    }
  }

  onHuntListClick(): void {
    this._router.navigate(['cucm-hunt-lists-report'], { relativeTo: this._route });
  }

  onRefreshClick(): void {
    this._refreshData();
  }

  onAutoAttendantReportFilter(filter: string): void {
    if (filter) {
      this._router.navigate(['cuc-auto-attendants-report', encodeURIComponent(filter)], {
        relativeTo: this._route,
      });
    } else {
      this._router.navigate(['cuc-auto-attendants-report'], { relativeTo: this._route });
    }
  }

  private _initContexts(): void {
    const sub = this._migrationsAssessmentsContext.state$
      .pipe(
        switchMap((migrations) => {
          this._assessment = migrations.ciscoPbxExports.find((migration) => migration.id === this._assessmentUuid);
          this.isUnityFileUploaded = this._assessment.isCucExportPresent;
          this.isCdrFileUploaded = this._assessment.isCucmCdrDumpPresent;
          this._setCallingProductDisabledText();

          if (this.isUnityFileUploaded && !this._migrationsCucAutoAttendantsContext) {
            this._migrationsCucAutoAttendantsContext = this._injector.get(MigrationsCucAutoAttendantsContext);
          }

          if (!this.isUnityFileUploaded) {
            this.autoAttendantsDisabledText = {
              header:
                'tkey;migrations.cisco.auto_attendants_report.dashboard.auto_attendant.no_unity_file.disabled.header',
              description:
                'tkey;migrations.cisco.auto_attendants_report.dashboard.auto_attendant.no_unity_file.disabled.description',
            };
          }

          const baseObservables = [
            this._migrationsCucmDirectoryNumberContext.jobState$,
            this._migrationsCucmHuntListsContext.jobState$,
          ];

          const jobStatusObservables = this.isUnityFileUploaded
            ? [...baseObservables, this._migrationsCucAutoAttendantsContext.jobState$]
            : baseObservables;

          return combineLatest(jobStatusObservables);
        }),
      )
      .subscribe(([dnsJobStatus, huntGroupsJobStatus, autoAttendantJobStatus]) => {
        this._initBreadcrumbs();
        this.isLoading = false;

        this._handleContextsPolling(
          dnsJobStatus,
          huntGroupsJobStatus,
          this.isUnityFileUploaded && autoAttendantJobStatus,
        );

        if (MigrationsService.shouldGetDnReport(dnsJobStatus, huntGroupsJobStatus)) {
          this.isDnReportWidgetError = MigrationsService.isErroredStatus(dnsJobStatus, huntGroupsJobStatus);
          if (!this.isDnReportWidgetError) {
            this._getDnReportData();
          } else {
            this.isDnReportDataRefreshing.set(false);
          }
        }

        if (MigrationsService.isHuntGroupIdle(huntGroupsJobStatus)) {
          this.isHuntListError = MigrationsService.isErroredHuntGroupStatus(huntGroupsJobStatus);
          if (!this.isHuntListError) {
            this._getHuntListData();
          } else {
            this.isHuntListRefreshing.set(false);
          }
        }

        if (this.isUnityFileUploaded) {
          if (MigrationsService.isAutoAttendantReportIdle(autoAttendantJobStatus)) {
            this.isAutoAttendantReportWidgetError = autoAttendantJobStatus.error !== null;
            if (!this.isAutoAttendantReportWidgetError) {
              this._getAutoAttendantsData();
            } else {
              this.isAutoAttendantRefreshing.set(false);
            }
          }
        }
      });
    this._subscription.add(sub);
  }

  private _refreshData(): void {
    if (this.isCdrFileUploaded && !this.cdrDiffDays) {
      this.cdrDiffDays = this.MIN_CDR_DAYS + 1;
    }
    this._dnReport = null;
    this._huntLists = null;
    this.migrationStatusData = [];
    this.usageTypeData = [];
    this.deviceTypeData = [];
    this.huntListData = [];
    this.suggestedProductData = [];
    this.autoAttendantsData = [];

    this.isDnReportDataRefreshing.set(true);
    this.isHuntListRefreshing.set(true);
    this.isAutoAttendantRefreshing.set(true);

    const requests = [];

    if (this._dnJobStatus === JobState.IDLE) {
      requests.push(this._migrationsCucmDirectoryNumberContext.refresh());
    }

    if (this._huntGroupJobStatus === JobState.IDLE) {
      requests.push(this._migrationsCucmHuntListsContext.refresh());
    }

    if (this.isUnityFileUploaded && this._autoAttendantJobStatus === JobState.IDLE) {
      requests.push(this._migrationsCucAutoAttendantsContext.refresh());
    }

    forkJoin(requests).subscribe({
      next: () => {
        this._toastService.push(
          ToastTypes.SUCCESS,
          ZiroIcons.REFRESH,
          'tkey;migrations.dashboard.refresh_toast.title',
          'tkey;migrations.dashboard.refresh_toast.message',
        );
      },
    });
  }

  private _handleContextsPolling(
    dnsJobStatus: JobStatus,
    huntGroupsJobStatus: JobStatus,
    autoAttendantJobStatus?: JobStatus | null,
  ): void {
    this._dnJobStatus = dnsJobStatus.jobState;
    this._huntGroupJobStatus = huntGroupsJobStatus.jobState;
    this._autoAttendantJobStatus = autoAttendantJobStatus?.jobState ?? null;

    const dnDataLoaded = !!this._dnReport;

    const isDnReportRefreshing =
      dnsJobStatus.jobState !== JobState.IDLE || huntGroupsJobStatus.jobState !== JobState.IDLE || !dnDataLoaded;

    const isHuntListRefreshing = huntGroupsJobStatus.jobState !== JobState.IDLE;

    const isAutoAttendantRefreshing =
      this.isUnityFileUploaded && !!autoAttendantJobStatus && autoAttendantJobStatus.jobState !== JobState.IDLE;

    this.isDnReportDataRefreshing.set(isDnReportRefreshing);
    this.isHuntListRefreshing.set(isHuntListRefreshing);
    this.isAutoAttendantRefreshing.set(isAutoAttendantRefreshing);
  }

  private _getDnReportData(): void {
    const subscription = this._migrationsCucmDirectoryNumberContext.state$.subscribe(
      (dnReport: CucmDirectoryNumbersReport) => {
        this._dnReport = dnReport;

        if (this._dnJobStatus === JobState.IDLE && this._huntGroupJobStatus === JobState.IDLE && !!this._dnReport) {
          this.isDnReportDataRefreshing.set(false);
        }

        this._calculateCdrDays();

        if (
          this._dnReport &&
          this._huntLists &&
          !this.usageTypeData.length &&
          !this.deviceTypeData.length &&
          !this.suggestedProductData.length
        ) {
          this._setChartData(dnReport);
        }
      },
    );
    this._subscription.add(subscription);
  }

  private _calculateCdrDays(): void {
    const oldestMoment = moment(new Date(this._dnReport.oldestCdrTimestamp));
    const newestMoment = moment(new Date(this._dnReport.mostRecentCdrTimestamp));
    this.cdrDiffDays = newestMoment.diff(oldestMoment, 'days');

    this._setCallingProductDisabledText();
  }

  private _setCallingProductDisabledText(): void {
    if (!this.isCdrFileUploaded) {
      this.cdrDiffDays = 0;
    }

    this.suggestedProductsDisabledText = this.isCdrFileUploaded
      ? {
          header:
            'tkey;migrations.cisco.directory_numbers_report.dashboard.suggested_calling_product.insufficient_cdr_days.disabled.header',
          description:
            'tkey;migrations.cisco.directory_numbers_report.dashboard.suggested_calling_product.disabled.description',
        }
      : {
          header:
            'tkey;migrations.cisco.directory_numbers_report.dashboard.suggested_calling_product.no_cdrs.disabled.header',
          description:
            'tkey;migrations.cisco.directory_numbers_report.dashboard.suggested_calling_product.disabled.description',
        };
  }

  private _getHuntListData(): void {
    const subscription = this._migrationsCucmHuntListsContext.state$.subscribe((huntLists: CucmHuntList[]) => {
      this._huntLists = huntLists;
      this._setHuntListData(huntLists);

      if (
        this._dnReport &&
        this._huntLists &&
        !this.usageTypeData.length &&
        !this.deviceTypeData.length &&
        !this.suggestedProductData.length
      ) {
        this._setChartData(this._dnReport);
      }
    });
    this._subscription.add(subscription);
  }

  private _setChartData(dnReport: CucmDirectoryNumbersReport): void {
    // Suggested Calling Product
    const sharedCallingLabel = this._translateService.instant(
      'tkey;migrations.cisco.directory_numbers_report.dashboard.suggested_calling_product.shared_calling.label',
    );
    const perMinuteLabel = this._translateService.instant(
      'tkey;migrations.cisco.directory_numbers_report.dashboard.suggested_calling_product.per_minute.label',
    );
    const bundledMinutesLabel = this._translateService.instant(
      'tkey;migrations.cisco.directory_numbers_report.dashboard.suggested_calling_product.bundled_minutes.label',
    );

    let sharedCallingCount = 0;
    let perMinuteCount = 0;
    let bundledMinutesCount = 0;

    dnReport.cucmDirectoryNumbers.forEach((directoryNumber) => {
      switch (directoryNumber.insights.suggestedCallingProduct) {
        case SuggestedProduct.SHARED_CALLING: {
          sharedCallingCount++;
          break;
        }
        case SuggestedProduct.PER_MINUTE: {
          perMinuteCount++;
          break;
        }
        case SuggestedProduct.BUNDLED_MINUTES: {
          bundledMinutesCount++;
          break;
        }
        default:
          break;
      }
    });

    this.migrationStatusData = this._createMigrationStatusChartData(dnReport.cucmDirectoryNumbers);
    this.usageTypeData = this._createUsageTypeChartData(dnReport.cucmDirectoryNumbers);
    this.deviceTypeData = this._createDeviceTypeChartData(dnReport.cucmDirectoryNumbers);

    this.suggestedProductData = [
      {
        name: sharedCallingLabel,
        value: sharedCallingCount,
        link: ['cucm-directory-numbers-report', `${'callingProduct'}=${encodeURIComponent(sharedCallingLabel)}`],
      },
      {
        name: perMinuteLabel,
        value: perMinuteCount,
        link: ['cucm-directory-numbers-report', `${'callingProduct'}=${encodeURIComponent(perMinuteLabel)}`],
      },
      {
        name: bundledMinutesLabel,
        value: bundledMinutesCount,
        link: ['cucm-directory-numbers-report', `${'callingProduct'}=${encodeURIComponent(bundledMinutesLabel)}`],
      },
    ];
  }

  private _setAutoAttendantChartData(autoAttendants: CucAutoAttendantsReport): void {
    const simpleScoreLabel = this._translateService.instant(
      'tkey;migrations.cisco.directory_numbers_report.dashboard.auto_attendant.simple_score.label',
    );
    const complexScoreLabel = this._translateService.instant(
      'tkey;migrations.cisco.directory_numbers_report.dashboard.auto_attendant.complex_score.label',
    );
    const requireInvestigationScoreLabel = this._translateService.instant(
      'tkey;migrations.cisco.directory_numbers_report.dashboard.auto_attendant.require_investigation_score.label',
    );

    const allHandlers = [
      ...(autoAttendants.callHandlers || []),
      ...(autoAttendants.directoryHandlers || []),
      ...(autoAttendants.interviewHandlers || []),
    ];

    const scoreCounts = allHandlers.reduce(
      (counts, handler) => {
        switch (handler.migrationComplexityScore) {
          case MigrationComplexityScore.SIMPLE:
            counts.simple++;
            break;
          case MigrationComplexityScore.COMPLEX:
            counts.complex++;
            break;
          case MigrationComplexityScore.REQUIRE_INVESTIGATION:
            counts.requireInvestigation++;
            break;
        }
        return counts;
      },
      { simple: 0, complex: 0, requireInvestigation: 0 },
    );

    this.autoAttendantsData = [
      {
        name: simpleScoreLabel,
        value: scoreCounts.simple,
        tooltipText: this._translateService.instant(
          'tkey;migrations.cisco.directory_numbers_report.dashboard.auto_attendant.simple_score.tooltip.text',
        ),
        link: ['cuc-auto-attendants-report', encodeURIComponent(simpleScoreLabel)],
      },
      {
        name: complexScoreLabel,
        value: scoreCounts.complex,
        tooltipText: this._translateService.instant(
          'tkey;migrations.cisco.directory_numbers_report.dashboard.auto_attendant.complex_score.tooltip.text',
        ),
        link: ['cuc-auto-attendants-report', encodeURIComponent(complexScoreLabel)],
      },
      {
        name: requireInvestigationScoreLabel,
        value: scoreCounts.requireInvestigation,
        tooltipText: this._translateService.instant(
          'tkey;migrations.cisco.directory_numbers_report.dashboard.auto_attendant.require_investigation_score.tooltip.text',
        ),
        link: ['cuc-auto-attendants-report', encodeURIComponent(requireInvestigationScoreLabel)],
      },
    ];
  }

  private _setHuntListData(huntLists: CucmHuntList[]): void {
    const allLineGroupMembers = huntLists.flatMap((obj) => obj.lineGroupMemberships);
    const uniqueLineGroupMemberships = Array.from(
      new Map(allLineGroupMembers.map((item) => [item.lineGroupName, item])).values(),
    );
    const allHuntPilots = huntLists.flatMap((obj) => obj.huntPilots);
    const huntListsCount = huntLists.length;

    this.huntListData = [
      {
        name: this._translateService.instant(
          huntListsCount === 1
            ? 'tkey;migrations.cisco.directory_numbers_report.dashboard.hunt_list.hunt_lists.label.singular'
            : 'tkey;migrations.cisco.directory_numbers_report.dashboard.hunt_list.hunt_lists.label.plural',
        ),
        value: huntListsCount,
      },
      {
        name: this._translateService.instant(
          uniqueLineGroupMemberships.length === 1
            ? 'tkey;migrations.cisco.directory_numbers_report.dashboard.hunt_list.line_groups.label.singular'
            : 'tkey;migrations.cisco.directory_numbers_report.dashboard.hunt_list.line_groups.label.plural',
        ),
        value: uniqueLineGroupMemberships.length,
      },
      {
        name: this._translateService.instant(
          allHuntPilots.length === 1
            ? 'tkey;migrations.cisco.directory_numbers_report.dashboard.hunt_list.hunt_pilots.label.singular'
            : 'tkey;migrations.cisco.directory_numbers_report.dashboard.hunt_list.hunt_pilots.label.plural',
        ),
        value: allHuntPilots.length,
      },
    ];
  }

  private _initBreadcrumbs(): void {
    this._dashboardService.updateDashboardBreadcrumbs(this._tenantId, this._assessment.name);
  }

  private _initBottomNav(): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonsList([
        {
          id: 'migrations-cisco-reports-list-back',
          dataAutomation: 'migrations-cisco-reports-list-back-button',
          label: 'tkey;global.button.back.text',
          buttonClass: ButtonStyles.DEFAULT,
          icon: ZiroIcons.BACK_CIRCLE,
          cb: () => this._router.navigate(['../../'], { relativeTo: this._route }),
        },
        {
          id: this._refreshButtonId,
          dataAutomation: 'migrations-cisco-reports-list-refresh-button',
          label: 'tkey;migrations.dashboard.refresh_button.label',
          icon: ZiroIcons.REFRESH,
          buttonClass: ButtonStyles.PRIMARY,
          state: {
            pending: false,
            buttonDisableState: { disabled: false },
          },
          cb: () => this.onRefreshClick(),
        },
      ]),
    );
  }

  private _getAutoAttendantsData(): void {
    const subscription = this._migrationsCucAutoAttendantsContext.state$.subscribe(
      (autoAttendants: CucAutoAttendantsReport) => {
        if (!this.autoAttendantsData.length) {
          this._setAutoAttendantChartData(autoAttendants);
        }
      },
    );
    this._subscription.add(subscription);
  }

  private _calculateTypeCounts(
    directoryNumbers: CucmDirectoryNumber[],
    type: 'lineUsageType' | 'deviceUsageType',
  ): Map<LineUsageType | DeviceUsageType, number> {
    const counts = new Map<LineUsageType | DeviceUsageType, number>();

    const values = type === 'lineUsageType' ? LineUsageType : DeviceUsageType;

    Object.values(values).forEach((usageType) => {
      counts.set(usageType, 0);
    });

    directoryNumbers.forEach((directoryNumber) => {
      const insightType = directoryNumber.insights[type];
      counts.set(insightType, (counts.get(insightType) || 0) + 1);
    });

    return counts;
  }

  private _createMigrationStatusChartData(directoryNumbers: CucmDirectoryNumber[]): ChartData[] {
    const statusCounts = new Map<MigrationStatus, number>();

    Object.values(MigrationStatus).forEach((status) => {
      statusCounts.set(status, 0);
    });

    directoryNumbers.forEach((dn) => {
      statusCounts.set(dn.migrationStatus, (statusCounts.get(dn.migrationStatus) || 0) + 1);
    });
    const migrationStatusLabels = new Map<MigrationStatus, string>([
      [MigrationStatus.MIGRATED, UiMigrationStatus.MIGRATED],
      [MigrationStatus.IGNORED, UiMigrationStatus.IGNORED],
      [MigrationStatus.NOT_READY, UiMigrationStatus.NOT_READY],
      [MigrationStatus.READY, UiMigrationStatus.READY],
      [MigrationStatus.STAGED, UiMigrationStatus.STAGED],
    ]);

    return Array.from(statusCounts.entries())
      .map(([status, count]) => ({
        name: migrationStatusLabels.get(status),
        value: count,
        link: [
          'cucm-directory-numbers-report',
          `${'migrationStatus'}=${encodeURIComponent(migrationStatusLabels.get(status))}`,
        ],
      }))
      .sort((a, b) => b.value - a.value);
  }

  private _createUsageTypeChartData(directoryNumbers: CucmDirectoryNumber[]): ChartData[] {
    const usageTypeCounts = this._calculateTypeCounts(directoryNumbers, 'lineUsageType') as Map<LineUsageType, number>;
    const usageTypeLabels = new Map<LineUsageType, string>([
      [LineUsageType.USER, UiLineType.USER],
      [LineUsageType.PUBLIC_PHONE, UiLineType.PUBLIC_PHONE],
      [LineUsageType.UCCX_AGENT, UiLineType.UCCX_AGENT],
      [LineUsageType.MULTI_LINE_USER, UiLineType.MULTI_LINE_USER],
      [LineUsageType.ANALOG, UiLineType.ANALOG],
      [LineUsageType.VOICE_APP, UiLineType.VOICE_APP],
      [LineUsageType.INACTIVE, UiLineType.INACTIVE],
      [LineUsageType.SHARED_LINE, UiLineType.SHARED_LINE],
      [LineUsageType.AUTO_ATTENDANT, UiLineType.AUTO_ATTENDANT],
      [LineUsageType.ORPHAN, UiLineType.ORPHAN],
      [LineUsageType.UNCLASSIFIED, UiLineType.UNCLASSIFIED],
    ]);

    return Array.from(usageTypeCounts.entries())
      .map(([usageType, count]) => ({
        name: usageTypeLabels.get(usageType) || usageType.toString(),
        value: count,
        link: [
          'cucm-directory-numbers-report',
          `${'lineType'}=${encodeURIComponent(usageTypeLabels.get(usageType) || usageType.toString())}`,
        ],
      }))
      .sort((a, b) => b.value - a.value);
  }

  private _createDeviceTypeChartData(directoryNumbers: CucmDirectoryNumber[]): ChartData[] {
    const deviceTypeCounts = this._calculateTypeCounts(directoryNumbers, 'deviceUsageType') as Map<
      DeviceUsageType,
      number
    >;
    const deviceTypeLabels = new Map<DeviceUsageType, string>([
      [DeviceUsageType.SOFTPHONE_ONLY, UiDeviceType.SOFTPHONE_ONLY],
      [DeviceUsageType.DESKPHONE, UiDeviceType.DESKPHONE],
      [DeviceUsageType.CTI_PORT, UiDeviceType.CTI_PORT],
      [DeviceUsageType.ANALOG_FAX, UiDeviceType.ANALOG_FAX],
      [DeviceUsageType.ANALOG_MODEM, UiDeviceType.ANALOG_MODEM],
      [DeviceUsageType.ANALOG_OTHER, UiDeviceType.ANALOG_OTHER],
      [DeviceUsageType.HOT_DESKING, UiDeviceType.HOT_DESKING],
      [DeviceUsageType.MIXED, UiDeviceType.MIXED],
      [DeviceUsageType.NO_DEVICE, UiDeviceType.NO_DEVICE],
    ]);

    return Array.from(deviceTypeCounts.entries())
      .map(([deviceType, count]) => ({
        name: deviceTypeLabels.get(deviceType) || deviceType.toString(),
        value: count,
        link: [
          'cucm-directory-numbers-report',
          `${'deviceType'}=${encodeURIComponent(deviceTypeLabels.get(deviceType) || deviceType.toString())}`,
        ],
      }))
      .sort((a, b) => b.value - a.value);
  }
}
